import { keyPressHelper } from '@brandfolder/utilities';
import React, {
  useEffect,
  useRef,
  FunctionComponent,
  KeyboardEvent,
  LiHTMLAttributes,
  SyntheticEvent,
  ReactNode,
  CSSProperties,
} from 'react';
import classnames from 'classnames';

import { BaseDropdownItemProps } from '@components/library/dropdown_item/DropdownItemProps';
import { FontIcon, FontIcons } from '@components/library/icon';

const baseName = 'dropdown-item';

const BaseDropdownItem: FunctionComponent<
BaseDropdownItemProps & LiHTMLAttributes<HTMLLIElement>
> = (props) => {
  const {
    children,
    className,
    icon = '',
    iconSize,
    id,
    indentation = 0,
    isArrowed,
    isDisabled = false,
    isDisplayed = true,
    isEmpty = false,
    isSelected,
    isTabbable = true,
    isWarningItem = false,
    name,
    onChoose,
    suppressScrollIntoView = false,
    styleClassNames = {}, // default value for testing (required prop)
    variantName = '', // default value for testing (required prop)
    ...liProps
  } = props;

  const itemRef = useRef<HTMLLIElement | null>(null);

  // Allows for negative values
  // Negative values will result in no indentation displayed (same as 0)
  // Values greather than 10 result in indentation of 10
  const indentationConstrained = indentation > 10 ? 10 : indentation;

  useEffect(() => {
    if (suppressScrollIntoView || !isArrowed || !itemRef.current) {
      return;
    }

    // Scroll the keyboard-highlighted option into view once when focus moves.
    // Do not use IntersectionObserver here — it fights manual wheel scrolling
    // by repeatedly calling scrollIntoView while the user scrolls the listbox.
    itemRef.current.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [isArrowed, suppressScrollIntoView]);

  const onKeyPressHandler = keyPressHelper(onChoose);

  if (!isDisplayed) return null;

  const renderIcon = (): ReactNode => {
    if (!icon) return null;

    const inlineIconStyle: CSSProperties = iconSize
      ? { fontSize: `${iconSize}px` }
      : undefined;

    if (icon.includes('bff-')) {
      return (
        <span
          aria-hidden
          className={`${icon} ${styleClassNames.icon || ''}`}
          role="img"
          style={inlineIconStyle}
        />
      );
    }
    return (
      <FontIcon
        aria-hidden
        className={styleClassNames.icon}
        icon={icon as FontIcons}
        style={inlineIconStyle}
      />
    );
  };

  const isActionItem = variantName === 'action';
  const itemRole = isActionItem ? 'menuitem' : 'option';

  return (
    <li
      {...liProps}
      ref={itemRef}
      aria-selected={!isActionItem && isSelected ? true : undefined}
      className={classnames(
        `bf-${baseName}__${variantName}`, // base-variant class for post consumption styling
        styleClassNames[variantName], // class for css modules
        className, // user specified class
        { [styleClassNames[`${variantName}-warning`]]: isWarningItem },
        { [styleClassNames.selected]: isSelected },
        { [styleClassNames.arrowed]: isArrowed },
        { [styleClassNames.disabled]: isDisabled },
        { [styleClassNames.empty]: isEmpty },
        { [styleClassNames[`indented${indentationConstrained}`]]: indentationConstrained }
      )}
      id={id || name?.toString()}
      onClick={(e: SyntheticEvent): void => {
        if (onChoose) {
          onChoose(name, e);
        }
      }}
      onKeyPress={(e: KeyboardEvent<HTMLLIElement>): void => {
        onKeyPressHandler(e, name);
      }}
      role={itemRole}
      tabIndex={isTabbable ? 0 : -1}
    >
      {icon && renderIcon()}
      {children}
    </li>
  );
};

export default BaseDropdownItem;
